

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../components/common/ExportLabel/Export';
import SearchBox from '../../components/common/Search/SearchBox';
import GroupBy from '../../components/common/GroupBy/GroupBy';
import Sort from '../../components/common/Sort/Sort';
import '../../components/common/SubNavbar.css';
import '../../components/common/CommonText.css';
import '../../components/common/groupby.css';
import '../../components/common/Table.css';
import TotalCount from '../../components/common/TotalCount/TotalCount';
import UserEditPopup from '../../components/Popup/UserEditPopup/UserEditPopup';
import RenewCountPopup from '../../components/Popup/RenewCount/RenewCount';
import { baseurl } from '../../api';
import { useCheckboxClick } from '../../checkboxclick';
import ArchiveSelectedItem from '../../components/common/ArchiveSelectedItemLabel/ArchiveSelectedItems';
import authenticatedAxios from '../../authenticatedAxios';
import useFilterStore from '../../store/useFilterStore';
import Filter from '../../components/common/Filter/Filter';
import Pagination from '../../components/common/Pagination/pagination';
const pageKey = "subscriptions"

const Subscriptions = () => {
  const { setFilters, getFilters } = useFilterStore();
  const savedFilters = getFilters(pageKey);
  const [selectedItem, setSelectedItem] = useState(null);
  const [items, setItems] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [renewPopupTitle, setRenewPopupTitle] = useState('');
  const [renewPopupDate, setRenewPopupDate] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isRenewCountPopupOpen, setIsRenewCountPopupOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState(savedFilters?.searchParams || { term: "" });
  const [selectedFilterOption, setSelectedFilterOption] = useState(savedFilters?.selectedFilterOption || "Default");
  const [selectedSortOption, setSelectedSortOption] = useState(savedFilters?.selectedSortOption || "Newest");
  const [selected, setSelected] = useState([null]);
  const [groupedItems, setGroupedItems] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupItem, setSelectedGroupItem] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectedGroupByOption, setSelectedGroupByOption] = useState('Default');
  const filterOptions = [
    { value: 'Default', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'expired', label: 'Expired' },
    { value: 'pending', label: 'Pending' },
  ];
  const [history, setHistory] = useState([]);
  const { isParentSelected, selectedItems, handleParentCheckboxChange, handleChildCheckboxChange, resetCheckboxState, } = useCheckboxClick(items);
  const [pagination, setPagination] = useState({});
  const [exportState, setExportState] = useState({
    isExporting: false,
    error: null
  });
  const sortOptions = ['Newest', 'Oldest'];
  const searchCategories = ['Company', 'Subscription Code'];
  const groupbyOptions = ['Default', 'Plans', 'Company', 'Status'];
  const [totalCount, setTotalCount] = useState(0);


  const fetchSubscriptions = async (page = null) => {
    try {
      const params = {
        search: searchParams.term,
        search_argument: searchParams.argument,
        filter: selectedFilterOption === 'Default' ? undefined : selectedFilterOption.toLowerCase(),
        status: ['active', 'expired', 'pending'].includes(selectedFilterOption) 
          ? selectedFilterOption 
          : undefined,
        sort: mapSortOptionToQuery(selectedSortOption),
        group_by: selectedGroupByOption.toLowerCase(),
        ...(page && { page })
      };

      const response = await authenticatedAxios.get(`${baseurl}subscriptions/`, { params });

      if (response.data.response_code === 1) {
        setItems(response.data.data);
        
        // Set total count from meta, not items.length
        if (response.data.meta?.total_count !== undefined) {
          setTotalCount(response.data.meta.total_count);
        }

        if (selectedGroupByOption !== 'Default') {
          groupItems(response.data.data, selectedGroupByOption);
        }
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      if (error?.response?.data?.error_code === 1001) {
        navigate("/permission-denied");
      } else {
        console.error('Error fetching subscriptions:', error);
      }
    }
  };

  const capitalizeFirst = (str) => {
    if (!str) return '';
    str = str.trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const groupItems = (data, groupBy) => {
    const grouped = data.reduce((acc, item) => {
      let key;
      switch (groupBy.toLowerCase()) {
        case 'plans':
          key = capitalizeFirst(item.plan_display_name);
          break;
        case 'company':
          key = item.organization.name;
          break;
        case 'status':
          key = item.status;
          break;
        default:
          key = 'default';
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});

    setGroupedItems(grouped);
  };

  useEffect(() => {
    fetchSubscriptions();
    setFilters(pageKey, {
      searchParams,
      selectedFilterOption,
      selectedSortOption,
    });
  }, [searchParams, selectedFilterOption, selectedGroupByOption, selectedSortOption]);

  const handleGroupBySelect = (option) => {
    setSelectedGroupByOption(option);
    setSelectedGroup(null);
    setSelectedGroupItem(null);
  };


  const fetchSubscriptionHistory = async (companyId, companyName, plan) => {
    try {
      const response = await authenticatedAxios.get(`${baseurl}subscriptions/history?company=${companyId}`);

      if (response.data.response_code === 1) {
        // Filter history to only show entries matching the selected plan
        const filteredHistory = response.data.data.filter(
          row => row.plan === plan
        );

        setSubscriptionHistory(filteredHistory);
        setRenewPopupTitle(`RENEW COUNT`);

        const activePlanStartDate = filteredHistory.find(row => row.status.toLowerCase() === 'active')?.start_date || 'N/A';
        setRenewPopupDate(activePlanStartDate);
        setIsRenewCountPopupOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchHistory = async (companyId) => {
    const { data } = await authenticatedAxios.get(`${baseurl}subscriptions/user-history/?subscription=${companyId}`);

    if (data.response_code === 1) {
      const formattedHistory = data.data.map(item => ({
        ...item,
        date: item.date.split("T")[0] // Extract only the date portion
      }));
      setHistory(formattedHistory);
    }
  };

  const handleArchiveSelectedItems = async (action) => {
    try {
      const response = await authenticatedAxios.post(`${baseurl}subscriptions/archive/`, {
        id_list: selectedItems,
      });

      if (response.status >= 200 && response.status < 300) {
        fetchSubscriptions();
        resetCheckboxState(); // Clear selection
      } else {
        console.error('Failed to archive/unarchive selected items.');
      }
    } catch (error) {
      console.error('Error archiving/unarchiving selected items:', error);
    }
  };
  const handleFilterSelect = (option) => {
    setSelectedFilterOption(option);
  };

  //   const ChangeUser = async (formValues) => {
  //     try {

  //         formValues.subscription = selected
  //         console.log("form",formValues)

  //         const response = await authenticatedAxios.post(`${baseurl}subscriptions/change-user-count/`, formValues, {
  //             headers: {
  //                 'Content-Type': 'application/json',
  //             }
  //         });

  //         if (response.status >= 200 && response.status < 300) {
  //             console.log('User change successful');
  //             closePopups(); // Close popup if required
  //             fetchSubscriptions();
  //         } else {
  //             console.error('Failed to change user');
  //         }
  //     } catch (error) {
  //         console.error('Error changing user:', error);
  //     }
  // };

  const groupSubscriptionsByCompanyPlan = (subscriptions) => {
    const grouped = {};

    subscriptions.forEach((subscription) => {
      const key = `${subscription.organization.id}_${subscription.plan}`;

      if (!grouped[key]) {
        grouped[key] = {
          ...subscription,
          renew_count: 1,
          allSubscriptions: [subscription],
        };
      } else {
        grouped[key].renew_count += 1;
        grouped[key].allSubscriptions.push(subscription);

        // Keep the most recent (active or latest) subscription's dates/status
        const currentDate = new Date(grouped[key].end_date);
        const newDate = new Date(subscription.end_date);
        if (newDate > currentDate) {
          grouped[key] = {
            ...grouped[key],
            start_date: subscription.start_date,
            end_date: subscription.end_date,
            status: subscription.status,
            billing_cycle: subscription.billing_cycle,
            active_users: subscription.active_users,
          };
        }
      }
    });

    return Object.values(grouped);
  };

  const ChangeUser = async (formValues) => {
    try {
      formValues.subscription = selected;

      const response = await authenticatedAxios.post(
        `${baseurl}subscriptions/change-user-count/`,
        formValues,
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.status >= 200 && response.status < 300) {
        closePopups();
        fetchSubscriptions();
      } else {
        console.error('Failed to change user');
      }
    } catch (error) {
      console.error('Error changing user:', error);
    }
  };



  const handleRenewCountClick = (companyId, companyName, plan) => {
    fetchSubscriptionHistory(companyId, companyName, plan);
  };

  const mapSortOptionToQuery = (option) => {
    switch (option) {
      case 'Newest':
        return 'newest';
      case 'Oldest':
        return 'oldest';
      default:
        return 'newest';
    }
  };

  const handlePageChange = (page) => {
    fetchSubscriptions(page);
  };

  const handleSortOptionSelect = (option) => {
    setSelectedSortOption(option);
  };

  const handleSearchSelect = (searchCategory, searchTerm) => {
    setSearchParams({
      term: searchTerm,
      argument: searchCategory.toLowerCase(),
    });
  };

  const handleDetailClick = (organizationId) => {
    if (organizationId) {
      navigate(`/customer-detail/${organizationId}`, { state: { from: 'subscriptions' } });
    } else {
      console.error('Organization ID is undefined');
    }
  };

  const handlePaymentClick = (organizationId, planName) => {
    navigate('/payments', {
      state: {
        organizationId,
        planName,
        fromSubscriptions: true
      }
    });
  };


  const paymentOptions = [
    // { value:'Payment',label:'--select--'},
    { value: 'upi', label: 'UPI' },
    { value: 'bank', label: 'Bank' },
  ];

  const closePopups = () => {
    setIsRenewCountPopupOpen(false);
    setIsPopupOpen(false);
    setSelectedItem(null);
  };
  const handleExportStart = () => {
    setExportState({ isExporting: true, error: null });
  };

  const handleExportComplete = () => {
    setExportState({ isExporting: false, error: null });
    resetCheckboxState();
  };

  const handleExportError = (error) => {
    setExportState({ isExporting: false, error });
  };

  // useEffect(() =>{
  //   console.log("Items",selected)
  // },[selected])

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };
  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }



  const renderGroupedTable = () => {
    if (selectedGroupByOption === 'Default') {
      return (
        <tbody className='common-table-tbody'>
          {renderSubscriptionRows(items)}
        </tbody>
      );
    }



    return (
      <tbody className='common-table-tbody'>
        {Object.entries(groupedItems).map(([groupName, groupSubscriptions]) => (
          <React.Fragment key={groupName}>
            <tr className="group-header-row">
              <td colSpan="6" className="group-header">
                <div
                  className="group-header-content"
                  onClick={() => toggleGroup(groupName)}
                >
                  <svg
                    className={`expand-item-icon ${expandedGroups.has(groupName) ? 'rotated' : ''}`}
                    width="7"
                    height="13"
                    viewBox="0 0 7 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7 6.5L0.25 12.9952L0.250001 0.00480908L7 6.5Z" fill="#424242" />
                  </svg>
                  <span className="group-name common-text fts-15 blue">{groupName}</span>
                  <span className="group-count common-text fts-15 blue">({groupSubscriptions.length})</span>
                </div>
              </td>
            </tr>
            {expandedGroups.has(groupName) && renderSubscriptionRows(groupSubscriptions)}
          </React.Fragment>
        ))}
      </tbody>
    );
  };

  const renderSubscriptionRows = (subscriptions) => {
    const grouped = groupSubscriptionsByCompanyPlan(subscriptions);

    return grouped.length > 0 ? (
      grouped.map((subscription) => (
        <tr key={`${subscription.organization.id}_${subscription.plan}`}>
          <td className='payment-td'>
            <input
              className="table-selectbox table-child-selectbox"
              type="checkbox"
              checked={selectedItems.includes(subscription.id)}
              onChange={() => handleChildCheckboxChange(subscription.id)}
            />
            {subscription.subscription_code}
            <svg
              className='payment-icon archive-icon'
              onClick={() => handlePaymentClick(subscription.organization.id, subscription.plan)}
              width="15" height="15" viewBox="0 0 15 15" fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 7.5C0 3.35781 3.35781 0 7.5 0C11.6422 0 15 3.35781 15 7.5C15 11.6422 11.6422 15 7.5 15C3.35781 15 0 11.6422 0 7.5ZM7.67906 8.045C8.60844 8.27375 8.88688 8.57687 8.88688 9.08875C8.88688 9.62062 8.52906 10.0231 7.67906 10.0878V8.045ZM7.1175 6.89719C6.3225 6.69812 6.02406 6.36531 6.02406 5.91281C6.02406 5.46031 6.40188 5.04812 7.11781 4.95875L7.1175 6.89719ZM7.67906 7.03125V4.95875C8.31531 5.03813 8.7625 5.40094 8.82719 6.0275H9.86094C9.82125 4.89937 8.92156 4.15375 7.67906 4.05406V3.21438H7.1175V4.05406C5.865 4.14875 4.97062 4.88406 4.97062 5.9975C4.97062 7.00625 5.65156 7.57313 6.91375 7.86625L7.1175 7.91594V10.0828C6.3175 9.99844 5.93969 9.57094 5.85031 8.98937H4.82156C4.85594 10.1822 5.85531 10.8928 7.1175 10.9875V11.8222H7.67906V10.9875C8.94656 10.8978 9.94063 10.2072 9.94063 8.98937C9.94063 7.93094 9.225 7.39406 7.91781 7.09094L7.67906 7.03125Z" fill="#094C80" />
            </svg>
          </td>

          <td onClick={() => handleDetailClick(subscription.organization.id)}>
            <span className="blue company-name">{subscription.organization.name}</span>
          </td>

          <td>{capitalizeFirst(subscription?.plan_display_name)}</td>

          <td>{subscription.is_freeplan ? "-" : capitalizeFirst(subscription.billing_cycle)}</td>

          <td>
            {formatDate(subscription.start_date)} to {formatDate(subscription.end_date)}
          </td>

          <td>
            <span className='green'>{subscription.active_users}</span>/{subscription.user_limit}

            {/* Only show settings icon if subscription is NOT expired */}
            {!subscription.is_expired && (
              <svg
                onClick={() => {
                  setSelected(subscription.id);
                  setIsPopupOpen(true);
                  fetchHistory(subscription.id);
                }}
                className='settings-icon'
                width="11" height="11" viewBox="0 0 11 11" fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M9.74239 5.5C9.74239 5.3735 9.73673 5.2525 9.72542 5.126L10.7775 4.3505C11.0038 4.1855 11.066 3.8775 10.9246 3.6355L9.86683 1.859C9.79901 1.7425 9.6893 1.65448 9.55879 1.61184C9.42829 1.5692 9.28618 1.57496 9.15977 1.628L7.94362 2.1285C7.73432 1.9855 7.51372 1.859 7.2818 1.7545L7.11776 0.484C7.08382 0.209 6.84059 0 6.55777 0H4.44789C4.15941 0 3.91618 0.209 3.88224 0.484L3.7182 1.7545C3.48628 1.859 3.26568 1.9855 3.05638 2.1285L1.84023 1.628C1.58003 1.518 1.27458 1.617 1.13317 1.859L0.0754002 3.641C-0.0660127 3.883 -0.00379103 4.1855 0.22247 4.356L1.27458 5.1315C1.25099 5.37847 1.25099 5.62703 1.27458 5.874L0.22247 6.6495C-0.00379103 6.8145 -0.0660127 7.1225 0.0754002 7.3645L1.13317 9.141C1.27458 9.383 1.58003 9.482 1.84023 9.372L3.05638 8.8715C3.26568 9.0145 3.48628 9.141 3.7182 9.2455L3.88224 10.516C3.91618 10.791 4.15941 11 4.44223 11H6.55211C6.83494 11 7.07817 10.791 7.11211 10.516L7.27615 9.2455C7.50806 9.141 7.72867 9.0145 7.93796 8.8715L9.15411 9.372C9.41431 9.482 9.71976 9.383 9.86117 9.141L10.9189 7.3645C11.0604 7.1225 10.9981 6.82 10.7719 6.6495L9.71976 5.874C9.73673 5.7475 9.74239 5.6265 9.74239 5.5ZM5.52263 7.425C4.43092 7.425 3.54285 6.5615 3.54285 5.5C3.54285 4.4385 4.43092 3.575 5.52263 3.575C6.61433 3.575 7.50241 4.4385 7.50241 5.5C7.50241 6.5615 6.61433 7.425 5.52263 7.425Z" fill="#094C80" />
              </svg>
            )}
          </td>

          <td className='renew-count-td'>
            {subscription.renew_count}
            <svg
              onClick={() => handleRenewCountClick(subscription.organization.id, subscription.organization.name, subscription.plan)}
              className='detail-icon'
              width="19" height="11" viewBox="0 0 19 11" fill="none"
            >
              <path d="M9.5 0C4.18985 0 0 5.49964 0 5.49964C0 5.49964 4.18985 11 9.5 11C13.5604 11 19 5.49964 19 5.49964C19 5.49964 13.5604 0 9.5 0ZM9.5 8.92605C7.63519 8.92605 6.1173 7.38894 6.1173 5.49964C6.1173 3.61034 7.63519 2.07253 9.5 2.07253C11.3648 2.07253 12.8827 3.61034 12.8827 5.49964C12.8827 7.38894 11.3648 8.92605 9.5 8.92605ZM9.5 3.49913C9.2376 3.49411 8.97685 3.54213 8.73299 3.64039C8.48912 3.73866 8.26704 3.88519 8.07972 4.07142C7.8924 4.25765 7.7436 4.47984 7.64203 4.72501C7.54045 4.97017 7.48813 5.2334 7.48813 5.49929C7.48813 5.76518 7.54045 6.0284 7.64203 6.27357C7.7436 6.51873 7.8924 6.74093 8.07972 6.92716C8.26704 7.11339 8.48912 7.25992 8.73299 7.35818C8.97685 7.45644 9.2376 7.50447 9.5 7.49945C10.0171 7.48956 10.5098 7.27448 10.8721 6.90045C11.2344 6.52643 11.4373 6.02332 11.4373 5.49929C11.4373 4.97526 11.2344 4.47215 10.8721 4.09812C10.5098 3.7241 10.0171 3.50902 9.5 3.49913Z" fill="#094C80" />
            </svg>
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td colSpan="6">No subscriptions found.</td>
      </tr>
    );
  };
  return (
    <div className='whole-container'>
      <div className='top-heading'>
        {/* <PageHeader pageName="Subscriptions "/> */}
        <PageHeader pageName="Subscription /" nestedpageName="Subscriptions" linkTo="/subscriptions" />
        <div className='export-container'>
          <ArchiveSelectedItem
            selectedItems={selectedItems}
            items={items}
            onArchiveSelected={handleArchiveSelectedItems}
          />
          <ExportLabel
            exportUrl={`${baseurl}subscriptions/export/`}
            selectedIds={selectedItems}
            onExportStart={handleExportStart}
            onExportComplete={handleExportComplete}
            onExportError={handleExportError}
          />
        </div>
      </div>
      <div className='pagename-container'>
        <div className='page-name'>
          <h1 className='common-text fts-22 ftw-6 blue'>Subscriptions</h1>
        </div>
        <div className='pagename-right-container'>
          <TotalCount TotalName="Total Subscriptions" TotalCount={totalCount} />
          <SearchBox categories={searchCategories} onSearchSelect={handleSearchSelect} defaultValue={searchParams?.term} />
          <GroupBy
            options={groupbyOptions}
            selectedOption={selectedGroupByOption}
            onSelect={handleGroupBySelect}
          />
          {/* <Filter filterOptions={filterOptions} onFilterSelect={handleFilterSelect} defaultValue={selectedFilterOption} /> */}
          <Sort options={sortOptions} onOptionSelect={handleSortOptionSelect} defaultValue={selectedSortOption} />
        </div>
      </div>


      <div className='table-container'>
        <table className='common-table'>
          <thead className='common-table-head'>
            <tr className='common-table-thead-tr'>
              <th className='w-20'>
                <input
                  className="table-selectbox table-parent-selectbox"
                  type="checkbox"
                  checked={isParentSelected}
                  onChange={handleParentCheckboxChange}
                />
                Subscription Code
              </th>
              <th className='w-15'>Company</th>
              <th className='w-10'>Plan</th>
              <th className='w-15'>Billing Cycle</th>
              <th className='w-20'>Start & End Date</th>
              <th className='w-10'>User Limit</th>
              <th className='w-10'>Renew Count</th>
            </tr>
          </thead>
          {renderGroupedTable()}
        </table>
      </div>

      {pagination && pagination.total_pages > 1 && (
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.total_pages}
          totalCount={pagination.total_items}
          perPage={pagination.items_per_page}
          onPageChange={handlePageChange}
        />
      )}

      <UserEditPopup
        isOpen={isPopupOpen}
        title="ADD/REMOVE USER"
        currentLimit={items.find(sub => sub.id === selected)?.organization?.user_limit}
        paymentMethod="upi"
        paymentOptions={paymentOptions}
        historyData={history}
        onSave={ChangeUser}
        onClose={closePopups}
      />

      <RenewCountPopup
        isOpen={isRenewCountPopupOpen}
        onClose={closePopups}
        title={renewPopupTitle}
        date={renewPopupDate}
        rows={subscriptionHistory}
      />

    </div>
  );
};

export default Subscriptions;
